/**
 * Camada 1 · campo de formulario.
 *
 * O rotulo e OBRIGATORIO no tipo, e nao opcional. Campo com placeholder no lugar de
 * rotulo perde o nome no instante em que alguem digita, e quem usa leitor de tela nunca
 * soube qual era. Deixar o rotulo opcional seria deixar essa escolha para cada tela.
 *
 * A marca de obrigatorio e o `required` do HTML andam JUNTOS, no mesmo argumento: o
 * asterisco sem o `required` deixa o formulario aceitar envio vazio, e foi assim que um
 * campo "obrigatorio" passou por revisao sem recusar nada.
 */

import { useId, type InputHTMLAttributes } from 'react';
import { t } from '@complexity/i18n';

export interface CampoProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  /**
   * O CAMPO OCUPA O ESPACO QUE SOBRA na linha, em vez da largura natural.
   *
   * Nasceu em 13/09/2026, do pedido dele: "campo email tem que ser mais largo". Numa
   * `LinhaDeCampos`, telefone e e-mail ficavam do mesmo tamanho, e e-mail e inerentemente
   * mais longo: `maria.aparecida@consultorio.com.br` nao cabe onde `+5561982743344` cabe.
   *
   * ELE MORA NO WRAPPER e nao no `<input>`, e por isso precisou ser prop: o input ja e
   * `width: 100%` (`styles.css:73`), entao classe passada por `className` nao alarga nada.
   *
   * A LARGURA MINIMA VAI JUNTO com o `flex-1`: sem ela, um campo que cresce pode ser
   * espremido abaixo do legivel quando a linha tem tres ou mais campos, e ai o remedio
   * produziria o defeito que ele existe para corrigir.
   */
  readonly cresce?: boolean;
  readonly rotulo: string;
  /** Marca visual, atributo required e anuncio ao leitor de tela, os tres de uma vez. */
  readonly obrigatorio?: boolean;
  /** Mensagem de erro. Presente, o campo fica invalido e a mensagem e associada a ele. */
  readonly erro?: string;
  /** Texto de apoio abaixo do campo. Escondido quando ha erro, para nao competir com ele. */
  readonly ajuda?: string;
  /**
   * RESERVA A LINHA DE MENSAGEM MESMO QUANDO NAO HA MENSAGEM.
   *
   * O DEFEITO QUE ELA RESOLVE, apontado por ele em 14/09/2026: *"os campos estao
   * desposicionando a medida que as mensagens abaixo vao aparecendo"*. MEDIDO na tela: o
   * campo de CPF estava em y=449 e SUBIU para y=429 no instante em que a mensagem "esse CPF
   * nao confere" nasceu embaixo dele. Vinte pixels, no meio da digitacao.
   *
   * A CAUSA E O ALINHAMENTO PELO FUNDO (`items-end`) SOMADO A ALTURA VARIAVEL: o bloco de
   * um campo e rotulo mais caixa mais mensagem, e sem mensagem ele encolhe. Alinhados pelo
   * fundo, os campos da linha ficam em alturas diferentes, e cada mensagem que aparece
   * REALINHA tudo.
   *
   * LIGADA, a linha existe sempre e so troca de conteudo: nada se move quando a mensagem
   * aparece ou some, e campos lado a lado ficam alinhados independentemente de qual deles
   * tem texto de apoio.
   *
   * NAO E O PADRAO de proposito: ligada em todo campo, ela somaria um espaco embaixo de
   * cada um em dezenas de telas que hoje estao certas. Quem liga e a tela que tem campos
   * LADO A LADO, que e onde o defeito acontece.
   */
  readonly reservaMensagem?: boolean;
}

export function Campo({
  rotulo,
  obrigatorio = false,
  erro,
  ajuda,
  reservaMensagem = false,
  cresce = false,
  className = '',
  ...resto
}: CampoProps) {
  // useId gera um id estavel entre servidor e cliente. Contador proprio produz id
  // diferente nos dois lados e quebra a associacao rotulo/campo na hidratacao.
  const id = useId();
  const idErro = `${id}-erro`;
  const idAjuda = `${id}-ajuda`;

  return (
    <div
      className={`flex flex-col gap-cx-1 ${cresce ? 'min-w-[16rem] flex-1' : ''}`}
    >
      <label htmlFor={id} className="text-cx-sm font-medium text-texto">
        {rotulo}
        {obrigatorio && (
          <>
            {/* O asterisco e decorativo: quem le com leitor de tela recebe a palavra
                inteira logo em seguida, porque "asterisco" nao significa nada falado. */}
            <span aria-hidden="true" className="ml-cx-1 text-erro">
              *
            </span>
            {/* PASSA PELO DICIONARIO, e ate 08/09/2026 nao passava. A classe `cx-so-leitor`
                esconde esta frase da tela: quem a recebe e quem usa leitor de tela, e
                por isso o defeito era invisivel. Com a tela inteira em ingles, a pessoa
                ouvia "Email, star, (obrigatório)". */}
            <span className="cx-so-leitor">{t('(obrigatório)')}</span>
          </>
        )}
      </label>

      <input
        id={id}
        className={`cx-campo ${className}`}
        required={obrigatorio}
        aria-invalid={erro ? true : undefined}
        // Aponta para a mensagem que existe: com erro, a de erro; sem erro, a de ajuda.
        aria-describedby={erro ? idErro : ajuda ? idAjuda : undefined}
        {...resto}
      />

      {erro && (
        // role="alert" faz o leitor de tela anunciar assim que a mensagem aparece, em vez
        // de so ao chegar no campo, que seria tarde para quem ja tentou enviar.
        <p id={idErro} role="alert" className="text-cx-xs text-erro">
          {erro}
        </p>
      )}

      {!erro && ajuda && (
        <p id={idAjuda} className="text-cx-xs text-texto-suave">
          {ajuda}
        </p>
      )}

      {/*
        O ESPACO RESERVADO, quando nao ha nem erro nem ajuda. Ver `reservaMensagem`.

        `aria-hidden` PORQUE ELE NAO DIZ NADA: um paragrafo vazio anunciado por leitor de
        tela e uma pausa sem conteudo, e a pessoa espera uma informacao que nao vem. A
        altura sai do mesmo `text-cx-xs` das outras duas linhas, entao as tres medem igual.
      */}
      {!erro && !ajuda && reservaMensagem && (
        <p aria-hidden="true" className="text-cx-xs text-texto-suave">
          &nbsp;
        </p>
      )}
    </div>
  );
}
